import { useState } from 'react'
import { supabase } from '../supabase'
import { exportArtefact } from './ExportPPT'

// ── Section and field components ───────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        background: 'var(--green-dark)', padding: '10px 20px',
        marginBottom: '16px'
      }}>
        <span style={{
          fontSize: '0.75rem', fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'var(--white)'
        }}>
          {title}
        </span>
      </div>
      <div style={{ padding: '0 4px' }}>
        {children}
      </div>
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {hint && (
        <div style={{
          fontSize: '0.75rem', color: 'var(--slate)',
          fontStyle: 'italic', marginBottom: '6px', marginTop: '-2px'
        }}>
          {hint}
        </div>
      )}
      {children}
    </div>
  )
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text" value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || ''}
    />
  )
}

function TextArea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      value={value || ''} rows={rows}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || ''}
      style={{ minHeight: rows * 24 + 'px' }}
    />
  )
}

function ScoreInput({ value, onChange, max = 10 }) {
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {Array.from({ length: max }, (_, i) => i + 1).map(n => (
        <button
          key={n}
          onClick={() => onChange(n)}
          style={{
            width: 36, height: 36,
            border: `1px solid ${parseInt(value) === n ? 'var(--green)' : 'var(--silver)'}`,
            background: parseInt(value) === n ? 'var(--green)' : 'var(--white)',
            color: parseInt(value) === n ? 'var(--white)' : 'var(--slate)',
            fontSize: '0.875rem', fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          {n}
        </button>
      ))}
    </div>
  )
}

// ── Artefact form definitions ──────────────────────────────────────────────

function KickoffPackForm({ content, onChange }) {
  const f = (key) => content[key] || ''
  const s = (key) => (val) => onChange({ ...content, [key]: val })

  return (
    <>
      <Section title="Opportunity Overview">
        <Field label="Opportunity Summary" hint="Two to three sentences describing the requirement and why we are pursuing it.">
          <TextArea value={f('opportunity_summary')} onChange={s('opportunity_summary')} placeholder="Summarise the opportunity, scope, and our strategic rationale for pursuing it." />
        </Field>
        <Field label="Key Evaluation Criteria" hint="What will the buyer be scoring us on? List the main criteria and any known weightings.">
          <TextArea value={f('evaluation_criteria')} onChange={s('evaluation_criteria')} placeholder="e.g. Technical approach 40%, Social value 20%, Price 30%, Management 10%" rows={3} />
        </Field>
        <Field label="Submission Deadline & Key Dates">
          <TextInput value={f('key_dates')} onChange={s('key_dates')} placeholder="e.g. ITT received 1 Apr, CQ deadline 15 Apr, Submission 30 Apr, Result June" />
        </Field>
      </Section>

      <Section title="Our Position">
        <Field label="Primary Win Theme" hint="The single most compelling reason we should win this bid.">
          <TextInput value={f('win_theme_primary')} onChange={s('win_theme_primary')} placeholder="e.g. Only supplier with live delivery at scale for this buyer type" />
        </Field>
        <Field label="Supporting Win Themes">
          <TextArea value={f('win_themes_supporting')} onChange={s('win_themes_supporting')} placeholder="List two to three supporting themes that reinforce the primary." rows={3} />
        </Field>
        <Field label="Key Discriminators" hint="What genuinely sets us apart from competitors on this bid?">
          <TextArea value={f('discriminators')} onChange={s('discriminators')} placeholder="List our strongest differentiators for this specific opportunity." rows={3} />
        </Field>
        <Field label="Known Risks to Winning">
          <TextArea value={f('risks_to_winning')} onChange={s('risks_to_winning')} placeholder="What could prevent us winning? Be honest." rows={3} />
        </Field>
      </Section>

      <Section title="Bid Team">
        <Field label="Bid Lead">
          <TextInput value={f('bid_lead')} onChange={s('bid_lead')} placeholder="Name and role" />
        </Field>
        <Field label="Solution Lead">
          <TextInput value={f('solution_lead')} onChange={s('solution_lead')} placeholder="Name and role" />
        </Field>
        <Field label="Commercial Lead">
          <TextInput value={f('commercial_lead')} onChange={s('commercial_lead')} placeholder="Name and role" />
        </Field>
        <Field label="Full Team Structure" hint="List all contributors and their workstream responsibilities.">
          <TextArea value={f('team_structure')} onChange={s('team_structure')} placeholder="Name — Role — Workstream — Time commitment" rows={5} />
        </Field>
      </Section>

      <Section title="Ground Rules">
        <Field label="Bid Rules & Protocols" hint="Review cadence, document control, version naming, sign-off process.">
          <TextArea value={f('ground_rules')} onChange={s('ground_rules')} placeholder="e.g. Daily standup 9am, all documents in SharePoint, no direct client contact without Bid Lead approval..." rows={4} />
        </Field>
        <Field label="Escalation Route">
          <TextInput value={f('escalation')} onChange={s('escalation')} placeholder="Who does the team escalate to for decisions above Bid Lead authority?" />
        </Field>
      </Section>
    </>
  )
}

function QuestionStoryboardForm({ content, onChange }) {
  const questions = content.questions || []

  const addQuestion = () => {
    onChange({
      ...content,
      questions: [...questions, {
        id: Date.now(),
        question_ref: '',
        question_text: '',
        word_limit: '',
        weighting: '',
        win_theme: '',
        discriminators: '',
        proof_points: '',
        response_structure: '',
        compliance_items: '',
        graphics_plan: '',
        assigned_writer: '',
        self_assessment: '',
        status: 'not_started'
      }]
    })
  }

  const updateQuestion = (idx, key, val) => {
    const updated = [...questions]
    updated[idx] = { ...updated[idx], [key]: val }
    onChange({ ...content, questions: updated })
  }

  const removeQuestion = (idx) => {
    const updated = questions.filter((_, i) => i !== idx)
    onChange({ ...content, questions: updated })
  }

  const STATUS_OPTS = ['not_started', 'in_progress', 'first_draft', 'in_review', 'complete']

  return (
    <>
      <Section title="Storyboard Overview">
        <Field label="ITT Reference">
          <TextInput value={content.itt_ref || ''} onChange={v => onChange({ ...content, itt_ref: v })} placeholder="e.g. ITT-2026-HMRC-001" />
        </Field>
        <Field label="Overall Response Strategy" hint="One paragraph on the overarching narrative thread across all questions.">
          <TextArea value={content.response_strategy || ''} onChange={v => onChange({ ...content, response_strategy: v })} placeholder="Describe the consistent narrative and win themes that should run through every question response." />
        </Field>
      </Section>

      <Section title="Questions">
        {questions.length === 0 && (
          <p style={{ color: 'var(--slate)', fontStyle: 'italic', marginBottom: '16px' }}>
            No questions added yet. Click Add Question to begin.
          </p>
        )}
        {questions.map((q, idx) => (
          <div key={q.id} style={{
            border: '1px solid var(--green-light)',
            padding: '20px', marginBottom: '16px',
            background: idx % 2 === 0 ? 'var(--white)' : 'var(--off-white)'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '16px'
            }}>
              <span style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1rem', fontWeight: 600, color: 'var(--green-dark)'
              }}>
                Question {idx + 1}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={q.status}
                  onChange={e => updateQuestion(idx, 'status', e.target.value)}
                  style={{
                    padding: '4px 8px', border: '1px solid var(--silver)',
                    fontSize: '0.75rem', fontWeight: 600,
                    color: 'var(--green)', borderRadius: 0, outline: 'none'
                  }}
                >
                  {STATUS_OPTS.map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </select>
                <button
                  onClick={() => removeQuestion(idx)}
                  style={{
                    background: 'none', border: '1px solid var(--silver)',
                    color: 'var(--slate)', padding: '3px 8px',
                    fontSize: '0.75rem', cursor: 'pointer'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                >
                  Remove
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <Field label="Question Reference">
                <TextInput value={q.question_ref} onChange={v => updateQuestion(idx, 'question_ref', v)} placeholder="e.g. Q4.2" />
              </Field>
              <Field label="Word / Page Limit">
                <TextInput value={q.word_limit} onChange={v => updateQuestion(idx, 'word_limit', v)} placeholder="e.g. 500 words" />
              </Field>
              <Field label="Weighting (%)">
                <TextInput value={q.weighting} onChange={v => updateQuestion(idx, 'weighting', v)} placeholder="e.g. 15%" />
              </Field>
            </div>

            <Field label="Question Text">
              <TextArea value={q.question_text} onChange={v => updateQuestion(idx, 'question_text', v)} placeholder="Paste the full question text here." rows={3} />
            </Field>
            <Field label="Win Theme for this Question">
              <TextInput value={q.win_theme} onChange={v => updateQuestion(idx, 'win_theme', v)} placeholder="The specific win theme this response should demonstrate." />
            </Field>
            <Field label="Discriminators" hint="What differentiates our answer to this question specifically?">
              <TextArea value={q.discriminators} onChange={v => updateQuestion(idx, 'discriminators', v)} placeholder="List the discriminators relevant to this question." rows={2} />
            </Field>
            <Field label="Proof Points & Evidence">
              <TextArea value={q.proof_points} onChange={v => updateQuestion(idx, 'proof_points', v)} placeholder="Case studies, statistics, accreditations, and past performance evidence to use." rows={3} />
            </Field>
            <Field label="Response Structure" hint="Outline the paragraph-by-paragraph structure before writing begins.">
              <TextArea value={q.response_structure} onChange={v => updateQuestion(idx, 'response_structure', v)} placeholder="Para 1: Introduction and win theme statement&#10;Para 2: Approach&#10;Para 3: Evidence&#10;Para 4: Added value&#10;Para 5: Summary" rows={5} />
            </Field>
            <Field label="Compliance Checklist" hint="What must be addressed to avoid a compliance fail?">
              <TextArea value={q.compliance_items} onChange={v => updateQuestion(idx, 'compliance_items', v)} placeholder="List each compliance requirement that must be addressed in this response." rows={2} />
            </Field>
            <Field label="Graphics Plan">
              <TextInput value={q.graphics_plan} onChange={v => updateQuestion(idx, 'graphics_plan', v)} placeholder="e.g. Process diagram showing delivery methodology, team org chart" />
            </Field>
            <Field label="Assigned Writer">
              <TextInput value={q.assigned_writer} onChange={v => updateQuestion(idx, 'assigned_writer', v)} placeholder="Name of the person responsible for this response" />
            </Field>
          </div>
        ))}
        <button
          className="btn btn--secondary"
          onClick={addQuestion}
          style={{ marginTop: '8px' }}
        >
          + Add Question
        </button>
      </Section>
    </>
  )
}

function BlueTeamForm({ content, onChange }) {
  const f = (key) => content[key] || ''
  const s = (key) => (val) => onChange({ ...content, [key]: val })

  return (
    <>
      <Section title="Session Details">
        <Field label="Session Date & Facilitator">
          <TextInput value={f('session_details')} onChange={s('session_details')} placeholder="e.g. 14 April 2026, facilitated by [name]" />
        </Field>
        <Field label="Attendees">
          <TextInput value={f('attendees')} onChange={s('attendees')} placeholder="List all participants" />
        </Field>
      </Section>

      <Section title="Win Theme Testing">
        <Field label="Primary Win Theme Under Review">
          <TextInput value={f('win_theme')} onChange={s('win_theme')} placeholder="State the win theme being tested" />
        </Field>
        <Field label="Evidence Supporting This Theme" hint="What proof do we have that this theme is genuinely true and credible?">
          <TextArea value={f('theme_evidence')} onChange={s('theme_evidence')} placeholder="List concrete evidence — past performance, accreditations, statistics, references." />
        </Field>
        <Field label="Challenges Raised" hint="What did the Blue Team push back on?">
          <TextArea value={f('challenges')} onChange={s('challenges')} placeholder="Record every challenge raised, however uncomfortable." />
        </Field>
        <Field label="Actions from Challenges">
          <TextArea value={f('challenge_actions')} onChange={s('challenge_actions')} placeholder="What needs to change or be strengthened as a result?" rows={3} />
        </Field>
      </Section>

      <Section title="Solution Challenge">
        <Field label="Solution Strengths Confirmed">
          <TextArea value={f('solution_strengths')} onChange={s('solution_strengths')} placeholder="What does the Blue Team agree is genuinely strong about our solution?" rows={3} />
        </Field>
        <Field label="Solution Weaknesses Identified">
          <TextArea value={f('solution_weaknesses')} onChange={s('solution_weaknesses')} placeholder="Where is our solution vulnerable or underdeveloped?" rows={3} />
        </Field>
        <Field label="Gaps to Address Before Submission">
          <TextArea value={f('solution_gaps')} onChange={s('solution_gaps')} placeholder="What must be resolved before we submit?" rows={3} />
        </Field>
      </Section>

      <Section title="Evaluator Perspective">
        <Field label="How Will the Evaluator Read Our Response?" hint="Put yourself in the evaluator's chair. What will they think?">
          <TextArea value={f('evaluator_view')} onChange={s('evaluator_view')} placeholder="Describe the evaluator's likely reaction to our current approach." />
        </Field>
        <Field label="What Scores Are We Likely to Achieve?" hint="Honest assessment, not wishful thinking.">
          <TextArea value={f('likely_scores')} onChange={s('likely_scores')} placeholder="Question by question honest score prediction and rationale." rows={3} />
        </Field>
        <Field label="What Would Improve Our Scores?">
          <TextArea value={f('score_improvements')} onChange={s('score_improvements')} placeholder="Specific changes that would improve evaluator scores." rows={3} />
        </Field>
      </Section>

      <Section title="Actions & Owners">
        <Field label="Action Log" hint="Every action from this session with owner and due date.">
          <TextArea value={f('action_log')} onChange={s('action_log')} placeholder="Action — Owner — Due Date&#10;Action — Owner — Due Date" rows={5} />
        </Field>
      </Section>
    </>
  )
}

function BlackHatForm({ content, onChange }) {
  const competitors = content.competitors || []

  const addCompetitor = () => {
    onChange({
      ...content,
      competitors: [...competitors, {
        id: Date.now(),
        name: '',
        win_themes: '',
        discriminators_against_us: '',
        their_weaknesses: '',
        ghost_strategy: '',
        threat_level: '3'
      }]
    })
  }

  const updateCompetitor = (idx, key, val) => {
    const updated = [...competitors]
    updated[idx] = { ...updated[idx], [key]: val }
    onChange({ ...content, competitors: updated })
  }

  const removeCompetitor = (idx) => {
    onChange({ ...content, competitors: competitors.filter((_, i) => i !== idx) })
  }

  return (
    <>
      <Section title="Our Vulnerabilities">
        <Field label="Our Biggest Weaknesses on This Bid" hint="Be brutally honest — what will competitors exploit?">
          <TextArea value={content.our_vulnerabilities || ''} onChange={v => onChange({ ...content, our_vulnerabilities: v })} placeholder="List our genuine weaknesses that a well-prepared competitor will identify and target." />
        </Field>
        <Field label="How We Mitigate or Address Each Weakness">
          <TextArea value={content.vulnerability_mitigation || ''} onChange={v => onChange({ ...content, vulnerability_mitigation: v })} placeholder="For each weakness above, what is our response or mitigation strategy?" />
        </Field>
      </Section>

      <Section title="Competitor Analysis">
        {competitors.length === 0 && (
          <p style={{ color: 'var(--slate)', fontStyle: 'italic', marginBottom: '16px' }}>
            No competitors added yet. Click Add Competitor to begin.
          </p>
        )}
        {competitors.map((comp, idx) => (
          <div key={comp.id} style={{
            border: '1px solid var(--green-light)', padding: '20px',
            marginBottom: '16px', background: idx % 2 === 0 ? 'var(--white)' : 'var(--off-white)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', fontWeight: 600, color: 'var(--green-dark)' }}>
                Competitor {idx + 1}
              </span>
              <button
                onClick={() => removeCompetitor(idx)}
                style={{ background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '3px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
              >Remove</button>
            </div>
            <Field label="Competitor Name">
              <TextInput value={comp.name} onChange={v => updateCompetitor(idx, 'name', v)} placeholder="Organisation name" />
            </Field>
            <Field label="Threat Level (1–5)" hint="1 = minimal threat, 5 = strongest competitor">
              <ScoreInput value={comp.threat_level} onChange={v => updateCompetitor(idx, 'threat_level', v)} max={5} />
            </Field>
            <Field label="Their Likely Win Themes" hint="What story will they tell? What will they lead with?">
              <TextArea value={comp.win_themes} onChange={v => updateCompetitor(idx, 'win_themes', v)} placeholder="How will this competitor position themselves to win?" rows={3} />
            </Field>
            <Field label="Their Discriminators Against Us" hint="Where are they genuinely stronger than us?">
              <TextArea value={comp.discriminators_against_us} onChange={v => updateCompetitor(idx, 'discriminators_against_us', v)} placeholder="What advantages does this competitor have over us on this bid?" rows={3} />
            </Field>
            <Field label="Their Weaknesses" hint="Where are they vulnerable?">
              <TextArea value={comp.their_weaknesses} onChange={v => updateCompetitor(idx, 'their_weaknesses', v)} placeholder="Where can we outperform this competitor?" rows={2} />
            </Field>
            <Field label="Our Ghost Strategy" hint="How do we make their strengths irrelevant and our strengths decisive?">
              <TextArea value={comp.ghost_strategy} onChange={v => updateCompetitor(idx, 'ghost_strategy', v)} placeholder="What specific moves in our bid strategy address this competitor's threat?" rows={3} />
            </Field>
          </div>
        ))}
        <button className="btn btn--secondary" onClick={addCompetitor} style={{ marginTop: '8px' }}>
          + Add Competitor
        </button>
      </Section>

      <Section title="Overall Competitive Strategy">
        <Field label="Our Competitive Position Summary">
          <TextArea value={content.competitive_summary || ''} onChange={v => onChange({ ...content, competitive_summary: v })} placeholder="Summarise our overall competitive position on this bid." />
        </Field>
        <Field label="Key Strategic Moves" hint="The specific things we will do differently because of this analysis.">
          <TextArea value={content.strategic_moves || ''} onChange={v => onChange({ ...content, strategic_moves: v })} placeholder="List the concrete changes to our bid strategy driven by this Black Hat analysis." rows={4} />
        </Field>
      </Section>
    </>
  )
}

function RedTeamForm({ content, onChange }) {
  const f = (key) => content[key] || ''
  const s = (key) => (val) => onChange({ ...content, [key]: val })
  const questions = content.question_reviews || []

  const addQuestion = () => {
    onChange({
      ...content,
      question_reviews: [...questions, {
        id: Date.now(),
        question_ref: '',
        score_out_of_10: '',
        compliance_pass: 'yes',
        win_theme_clear: 'yes',
        strengths: '',
        improvements: '',
        must_fix: ''
      }]
    })
  }

  const updateQuestion = (idx, key, val) => {
    const updated = [...questions]
    updated[idx] = { ...updated[idx], [key]: val }
    onChange({ ...content, question_reviews: updated })
  }

  const removeQuestion = (idx) => {
    onChange({ ...content, question_reviews: questions.filter((_, i) => i !== idx) })
  }

  return (
    <>
      <Section title="Review Details">
        <Field label="Review Date & Chair">
          <TextInput value={f('review_details')} onChange={s('review_details')} placeholder="e.g. 22 April 2026, chaired by [name]" />
        </Field>
        <Field label="Reviewers">
          <TextInput value={f('reviewers')} onChange={s('reviewers')} placeholder="List all Red Team reviewers" />
        </Field>
        <Field label="Overall Impression" hint="The Red Team's honest first read of the full response.">
          <TextArea value={f('overall_impression')} onChange={s('overall_impression')} placeholder="What is the overall quality and impact of the response? Does it read as a winner?" />
        </Field>
      </Section>

      <Section title="Question Reviews">
        {questions.length === 0 && (
          <p style={{ color: 'var(--slate)', fontStyle: 'italic', marginBottom: '16px' }}>
            No question reviews added yet. Click Add Question Review to begin.
          </p>
        )}
        {questions.map((q, idx) => (
          <div key={q.id} style={{
            border: '1px solid var(--green-light)', padding: '20px',
            marginBottom: '16px',
            background: idx % 2 === 0 ? 'var(--white)' : 'var(--off-white)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', fontWeight: 600, color: 'var(--green-dark)' }}>
                Question Review {idx + 1}
              </span>
              <button
                onClick={() => removeQuestion(idx)}
                style={{ background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '3px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
              >Remove</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <Field label="Question Reference">
                <TextInput value={q.question_ref} onChange={v => updateQuestion(idx, 'question_ref', v)} placeholder="e.g. Q4.2" />
              </Field>
              <Field label="Score (1–10)">
                <input
                  type="number" min="1" max="10"
                  value={q.score_out_of_10}
                  onChange={e => updateQuestion(idx, 'score_out_of_10', e.target.value)}
                  style={{ width: '80px', padding: '8px', border: '1px solid var(--silver)', borderRadius: 0, outline: 'none', fontSize: '1rem', fontWeight: 600, color: 'var(--green-dark)', textAlign: 'center' }}
                />
              </Field>
              <Field label="Compliance Pass?">
                <select value={q.compliance_pass} onChange={e => updateQuestion(idx, 'compliance_pass', e.target.value)}
                  style={{ padding: '8px', border: '1px solid var(--silver)', borderRadius: 0, outline: 'none', width: '100%' }}>
                  <option value="yes">Yes</option>
                  <option value="no">No — action required</option>
                  <option value="partial">Partial</option>
                </select>
              </Field>
              <Field label="Win Theme Clear?">
                <select value={q.win_theme_clear} onChange={e => updateQuestion(idx, 'win_theme_clear', e.target.value)}
                  style={{ padding: '8px', border: '1px solid var(--silver)', borderRadius: 0, outline: 'none', width: '100%' }}>
                  <option value="yes">Yes</option>
                  <option value="no">Not clear</option>
                  <option value="partial">Partially</option>
                </select>
              </Field>
            </div>
            <Field label="Strengths">
              <TextArea value={q.strengths} onChange={v => updateQuestion(idx, 'strengths', v)} placeholder="What is working well in this response?" rows={2} />
            </Field>
            <Field label="Improvements Required">
              <TextArea value={q.improvements} onChange={v => updateQuestion(idx, 'improvements', v)} placeholder="What should be changed or strengthened?" rows={2} />
            </Field>
            <Field label="Must Fix Before Submission" hint="Non-negotiable changes — this response cannot be submitted without these.">
              <TextArea value={q.must_fix} onChange={v => updateQuestion(idx, 'must_fix', v)} placeholder="List any critical issues that are blockers to submission." rows={2} />
            </Field>
          </div>
        ))}
        <button className="btn btn--secondary" onClick={addQuestion} style={{ marginTop: '8px' }}>
          + Add Question Review
        </button>
      </Section>

      <Section title="Overall Assessment">
        <Field label="Response Strengths Summary">
          <TextArea value={f('overall_strengths')} onChange={s('overall_strengths')} placeholder="What is the response doing well across all questions?" rows={3} />
        </Field>
        <Field label="Response Weaknesses Summary">
          <TextArea value={f('overall_weaknesses')} onChange={s('overall_weaknesses')} placeholder="What are the consistent weaknesses across the response?" rows={3} />
        </Field>
        <Field label="Priority Actions Before Submission">
          <TextArea value={f('priority_actions')} onChange={s('priority_actions')} placeholder="The ordered list of must-do improvements before we submit." rows={4} />
        </Field>
        <Field label="Red Team Recommendation">
          <select
            value={f('recommendation')}
            onChange={e => onChange({ ...content, recommendation: e.target.value })}
            style={{ padding: '10px 14px', border: '1px solid var(--silver)', borderRadius: 0, outline: 'none', width: '100%' }}
          >
            <option value="">Select recommendation</option>
            <option value="submit">Submit — response is ready</option>
            <option value="submit_with_changes">Submit with changes — address actions above</option>
            <option value="major_rework">Major rework required before submission</option>
          </select>
        </Field>
      </Section>
    </>
  )
}

function SignOffPackForm({ content, onChange }) {
  const f = (key) => content[key] || ''
  const s = (key) => (val) => onChange({ ...content, [key]: val })

  return (
    <>
      <Section title="Opportunity Recap">
        <Field label="Opportunity Summary" hint="What are we bidding for, with whom, and why?">
          <TextArea value={f('opportunity_recap')} onChange={s('opportunity_recap')} placeholder="Buyer, contract scope, term, framework context, and our strategic rationale." />
        </Field>
        <Field label="Qualification Score & PWin">
          <TextInput value={f('qual_summary')} onChange={s('qual_summary')} placeholder="e.g. Qualification score 3.85 / 5.00 — Pursue. PWin 35%." />
        </Field>
        <Field label="Our Recommendation to Submit">
          <select
            value={f('submit_recommendation')}
            onChange={e => onChange({ ...content, submit_recommendation: e.target.value })}
            style={{ padding: '10px 14px', border: '1px solid var(--silver)', borderRadius: 0, outline: 'none', width: '100%' }}
          >
            <option value="">Select recommendation</option>
            <option value="recommend_submit">Recommend submission — unconditional</option>
            <option value="recommend_submit_conditions">Recommend submission — with conditions noted below</option>
            <option value="do_not_recommend">Do not recommend submission</option>
          </select>
        </Field>
      </Section>

      <Section title="Solution Summary">
        <Field label="Our Proposed Solution" hint="Two to three paragraphs. What are we proposing to deliver and how?">
          <TextArea value={f('solution_summary')} onChange={s('solution_summary')} placeholder="Describe our proposed solution, delivery approach, and key differentiators." rows={5} />
        </Field>
        <Field label="Key Personnel">
          <TextArea value={f('key_personnel')} onChange={s('key_personnel')} placeholder="Name — Role — Relevant experience" rows={3} />
        </Field>
        <Field label="Mobilisation Approach">
          <TextArea value={f('mobilisation')} onChange={s('mobilisation')} placeholder="How will we mobilise if we win? Key dependencies and timeline." rows={3} />
        </Field>
      </Section>

      <Section title="Commercial Position">
        <Field label="Total Bid Price (£)">
          <TextInput value={f('bid_price')} onChange={s('bid_price')} placeholder="e.g. £4,750,000" />
        </Field>
        <Field label="Estimated Cost (£)">
          <TextInput value={f('estimated_cost')} onChange={s('estimated_cost')} placeholder="e.g. £3,800,000" />
        </Field>
        <Field label="Gross Margin (£ and %)">
          <TextInput value={f('gross_margin')} onChange={s('gross_margin')} placeholder="e.g. £950,000 — 20%" />
        </Field>
        <Field label="Key Commercial Assumptions" hint="What assumptions underpin our pricing? If these change, margin changes.">
          <TextArea value={f('commercial_assumptions')} onChange={s('commercial_assumptions')} placeholder="List the critical assumptions in our cost model." rows={3} />
        </Field>
        <Field label="Pricing Confidence" hint="How confident are we that our price is competitive?">
          <TextArea value={f('pricing_confidence')} onChange={s('pricing_confidence')} placeholder="Describe our price position relative to the market and any competitive intelligence." rows={2} />
        </Field>
      </Section>

      <Section title="Risk Summary">
        <Field label="Top Risks" hint="List the top five risks with likelihood, impact, and mitigation.">
          <TextArea value={f('top_risks')} onChange={s('top_risks')} placeholder="Risk — Likelihood — Impact — Mitigation&#10;Risk — Likelihood — Impact — Mitigation" rows={6} />
        </Field>
        <Field label="Unmitigated or Accepted Risks" hint="Risks we are accepting rather than mitigating — flag these explicitly.">
          <TextArea value={f('accepted_risks')} onChange={s('accepted_risks')} placeholder="Any risks we have consciously decided to accept rather than mitigate." rows={3} />
        </Field>
      </Section>

      <Section title="Commercial & Legal Considerations">
        <Field label="Onerous Terms Identified" hint="Contract terms that represent unusual risk or liability.">
          <TextArea value={f('onerous_terms')} onChange={s('onerous_terms')} placeholder="List any contract terms that are onerous, unusual, or represent significant risk." rows={3} />
        </Field>
        <Field label="Deviations Requested or Accepted">
          <TextArea value={f('deviations')} onChange={s('deviations')} placeholder="What contract deviations have we requested? What has the buyer accepted?" rows={2} />
        </Field>
        <Field label="Liability Position">
          <TextInput value={f('liability')} onChange={s('liability')} placeholder="e.g. Liability capped at contract value. IP ownership retained by client." />
        </Field>
        <Field label="TUPE Implications">
          <TextArea value={f('tupe')} onChange={s('tupe')} placeholder="Are there TUPE implications? If so, what is the assessed cost and risk?" rows={2} />
        </Field>
        <Field label="Legal Sign-Off Obtained?">
          <select
            value={f('legal_signoff')}
            onChange={e => onChange({ ...content, legal_signoff: e.target.value })}
            style={{ padding: '10px 14px', border: '1px solid var(--silver)', borderRadius: 0, outline: 'none', width: '100%' }}
          >
            <option value="">Select</option>
            <option value="yes">Yes — legal has reviewed and signed off</option>
            <option value="in_progress">In progress — review underway</option>
            <option value="not_required">Not required for this bid</option>
            <option value="no">No — not yet reviewed</option>
          </select>
        </Field>
      </Section>

      <Section title="Authority Sign-Off">
        <Field label="Authorising Individual or Body">
          <TextInput value={f('authority')} onChange={s('authority')} placeholder="e.g. CEO, Bid Board, Managing Director" />
        </Field>
        <Field label="Conditions on Approval (if any)">
          <TextArea value={f('approval_conditions')} onChange={s('approval_conditions')} placeholder="Any conditions placed on approval — e.g. legal review must be completed before submission." rows={2} />
        </Field>
        <Field label="Decision">
          <select
            value={f('decision')}
            onChange={e => onChange({ ...content, decision: e.target.value })}
            style={{ padding: '10px 14px', border: '1px solid var(--silver)', borderRadius: 0, outline: 'none', width: '100%' }}
          >
            <option value="">Awaiting decision</option>
            <option value="approved">Approved — proceed to submission</option>
            <option value="approved_conditions">Approved with conditions</option>
            <option value="deferred">Deferred — further information required</option>
            <option value="rejected">Not approved — do not submit</option>
          </select>
        </Field>
        <Field label="Decision Date">
          <input
            type="date"
            value={f('decision_date')}
            onChange={e => onChange({ ...content, decision_date: e.target.value })}
          />
        </Field>
        <Field label="Decision Notes">
          <TextArea value={f('decision_notes')} onChange={s('decision_notes')} placeholder="Any notes or commentary from the authorising body." rows={3} />
        </Field>
      </Section>
    </>
  )
}

// ── Form router ────────────────────────────────────────────────────────────
function ArtefactForm({ type, content, onChange }) {
  switch (type) {
    case 'kickoff_pack':       return <KickoffPackForm content={content} onChange={onChange} />
    case 'question_storyboard': return <QuestionStoryboardForm content={content} onChange={onChange} />
    case 'blue_team':          return <BlueTeamForm content={content} onChange={onChange} />
    case 'black_hat':          return <BlackHatForm content={content} onChange={onChange} />
    case 'red_team':           return <RedTeamForm content={content} onChange={onChange} />
    case 'sign_off_pack':      return <SignOffPackForm content={content} onChange={onChange} />
    default: return <p>Unknown artefact type.</p>
  }
}

// ── Artefact type config (duplicated for use in editor) ────────────────────
const ARTEFACT_TYPES = {
  kickoff_pack:        { label: 'Bid Kick-off Pack',     icon: '🚀', colour: '1A4035' },
  question_storyboard: { label: 'Question Storyboard',   icon: '📋', colour: '2D6A4F' },
  blue_team:           { label: 'Blue Team Review',       icon: '🔵', colour: '2B6CB0' },
  black_hat:           { label: 'Black Hat Review',       icon: '🎩', colour: '1C1C1C' },
  red_team:            { label: 'Red Team Review',        icon: '🔴', colour: 'C0392B' },
  sign_off_pack:       { label: 'Sign-Off Pack',          icon: '✅', colour: 'C9A84C' },
}

// ── Main editor component ──────────────────────────────────────────────────
function ArtefactEditor({ artefact, opportunity, session, onSaved, onBack }) {
  const [content, setContent] = useState(artefact.content || {})
  const [title, setTitle] = useState(artefact.title || '')
  const [status, setStatus] = useState(artefact.status || 'draft')
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [error, setError] = useState(null)

  const typeConfig = ARTEFACT_TYPES[artefact.type]

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const { data, error } = await supabase
      .from('artefacts')
      .update({ title, status, content })
      .eq('id', artefact.id)
      .select()
      .single()
    setSaving(false)
    if (error) { setError(error.message); return }
    setLastSaved(new Date())
    if (data) onSaved(data)
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>

      {/* Editor header */}
      <div style={{
        background: 'var(--green-dark)', padding: '20px 24px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{ fontSize: '1.25rem' }}>{typeConfig?.icon}</span>
              <span style={{
                fontSize: '0.625rem', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'var(--green-light)'
              }}>
                {typeConfig?.label}
              </span>
              {opportunity && (
                <span style={{ fontSize: '0.75rem', color: 'var(--silver)' }}>
                  · {opportunity.title}
                </span>
              )}
            </div>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'var(--white)', padding: '8px 12px',
                fontSize: '1.25rem',
                fontFamily: 'Cormorant Garamond, serif',
                fontWeight: 600, borderRadius: 0, outline: 'none',
                width: '100%', maxWidth: '560px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px', flexShrink: 0 }}>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              style={{
                padding: '6px 10px', border: '1px solid rgba(255,255,255,0.3)',
                fontSize: '0.8125rem', fontWeight: 600,
                color: 'var(--white)', background: 'rgba(255,255,255,0.1)',
                borderRadius: 0, outline: 'none', cursor: 'pointer',
                colorScheme: 'dark'
              }}
            >
              <option value="draft">Draft</option>
              <option value="in_progress">In Progress</option>
              <option value="complete">Complete</option>
              <option value="approved">Approved</option>
            </select>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: 'var(--green)', border: 'none',
                  color: 'var(--white)', padding: '8px 20px',
                  fontSize: '0.875rem', fontWeight: 600,
                  cursor: 'pointer', transition: 'background 0.2s'
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={onBack}
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'var(--silver)', padding: '8px 16px',
                  fontSize: '0.875rem', cursor: 'pointer'
                }}
              >
                ← Back
              </button>
            </div>
            {lastSaved && (
              <div style={{ fontSize: '0.75rem', color: 'var(--green-light)' }}>
                Saved {lastSaved.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: '16px' }}>{error}</div>}

      {/* Artefact form */}
      <ArtefactForm
        type={artefact.type}
        content={content}
        onChange={setContent}
      />

      {/* Bottom save bar */}
      <div style={{
        position: 'sticky', bottom: 0,
        background: 'var(--white)',
        borderTop: '2px solid var(--green-light)',
        padding: '14px 0',
        display: 'flex', gap: '12px', alignItems: 'center',
        marginTop: '32px'
      }}>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn--primary"
        >
          {saving ? 'Saving...' : 'Save Artefact'}
        </button>
        <button
          onClick={onBack}
          className="btn btn--secondary"
        >
          ← Back to Artefacts
        </button>
        <button
          onClick={() => exportArtefact({ ...artefact, content }, { title: artefact.title })}
          className="btn btn--secondary"
          style={{ borderColor: 'var(--gold)', color: 'var(--gold-dark)' }}
        >
          Export PPT ↓
        </button>
        {lastSaved && (
          <span style={{ fontSize: '0.8125rem', color: 'var(--slate)', fontStyle: 'italic' }}>
            Last saved at {lastSaved.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
    </div>
  )
}

export default ArtefactEditor